import { inject, Injectable, InjectionToken } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { catchError, defer, EMPTY, filter, firstValueFrom, from, map, Observable, shareReplay, switchMap } from 'rxjs';

/**
 * app#397 — how the webapp obtains a Google ID token.
 *
 * `popup`: Google Identity Services renders its button on the app's own origin, which must be an
 * authorized JavaScript origin of the OAuth client. prod/test/demo and local `pnpm start`.
 *
 * `relay`: the browser is sent to auth.fsn (`/google-relay/start`), which runs the Google flow with
 * the ONE redirect URI Google knows and comes back to the page it left with a single-use `code`.
 * The page swaps the code for the raw Google ID token with a credential-less GET. Every
 * `dev-<slug>.fsn.supplycanvas.com` preview works without a console entry. The server verifies
 * the token exactly as in popup mode — the relay changes only how the browser gets hold of it.
 */
export type GoogleLoginMode = 'popup' | 'relay';

export interface AuthProviderInfo {
  googleClientId: string;
  googleLoginMode?: GoogleLoginMode | string;
  relayUrl?: string | null;
}

export type GoogleSignInEvent = { idToken: string } | { error: string };

/** sessionStorage key of the login-CSRF state the relay round trip must bring back. */
export const RELAY_STATE_KEY = 'googleRelayState';

/**
 * E3b: `localhost` is deliberately NOT in auth.fsn's allowlist — local development keeps popup
 * mode (`http://localhost:4200` is an authorized JS origin), whatever the backend reports.
 */
export function resolveGoogleLoginMode(info: AuthProviderInfo | undefined, hostname: string): GoogleLoginMode {
  if (hostname === 'localhost') {
    return 'popup';
  }
  return info?.googleLoginMode === 'relay' && !!info.relayUrl ? 'relay' : 'popup';
}

/** The browser pieces the relay leg touches — injectable so specs run without a real window. */
export interface RelayBrowser {
  readonly href: string;
  readonly hostname: string;
  /** Rewrite the current URL without a navigation (`history.replaceState`). */
  replaceUrl(url: string): void;
  /** Full-page navigation. */
  navigate(url: string): void;
  getStored(): string | null;
  setStored(value: string): void;
  clearStored(): void;
  fetch: typeof fetch;
  /** ≥128 random bits as hex. */
  randomHex(): string;
}

export const GOOGLE_RELAY_BROWSER = new InjectionToken<RelayBrowser>('GOOGLE_RELAY_BROWSER', {
  providedIn: 'root',
  factory: (): RelayBrowser => ({
    get href() {
      return window.location.href;
    },
    get hostname() {
      return window.location.hostname;
    },
    replaceUrl: (url) => window.history.replaceState(window.history.state, '', url),
    navigate: (url) => window.location.assign(url),
    getStored: () => safeStorage(() => window.sessionStorage.getItem(RELAY_STATE_KEY)) ?? null,
    setStored: (value) => safeStorage(() => window.sessionStorage.setItem(RELAY_STATE_KEY, value)),
    clearStored: () => safeStorage(() => window.sessionStorage.removeItem(RELAY_STATE_KEY)),
    fetch: (input, init) => window.fetch(input, init),
    randomHex: () => {
      const bytes = window.crypto.getRandomValues(new Uint8Array(16));
      return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    },
  }),
});

function safeStorage<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    // sessionStorage can throw (private mode, blocked storage) — the relay then fails closed:
    // no stored state means the return leg is refused.
    return undefined;
  }
}

/** The query params the relay round trip adds to the page URL; stripped on return, never forwarded. */
export const RELAY_PARAMS = ['code', 'relayState', 'relay_error'] as const;

const providerCache = new WeakMap<HttpClient, Observable<AuthProviderInfo>>();

/**
 * ONE `GET api/auth/provider` per app load, shared by the social-login config factory (bootstrap)
 * and the service — RxJS 7 `shareReplay(1)` resets on error, so a failed fetch is retried by the
 * next subscriber rather than cached.
 */
export function providerInfo(http: HttpClient): Observable<AuthProviderInfo> {
  let cached = providerCache.get(http);
  if (!cached) {
    cached = http.get<AuthProviderInfo>('api/auth/provider').pipe(shareReplay(1));
    providerCache.set(http, cached);
  }
  return cached;
}

@Injectable({ providedIn: 'root' })
export class GoogleIdTokenService {
  private readonly http = inject(HttpClient);
  private readonly socialAuthService = inject(SocialAuthService);
  private readonly browser = inject(GOOGLE_RELAY_BROWSER);

  private readonly provider$ = providerInfo(this.http);
  private readonly mode$ = this.provider$.pipe(map((info) => resolveGoogleLoginMode(info, this.browser.hostname)));

  mode(): Promise<GoogleLoginMode> {
    return firstValueFrom(this.mode$);
  }

  /**
   * The ONE entry point for the login and register pages: every Google sign-in — the GIS popup's
   * result in popup mode, the relayed token on the return leg in relay mode — arrives here as
   * `{ idToken }`; a relay that came back without a token arrives as `{ error }`.
   */
  signIns(): Observable<GoogleSignInEvent> {
    return this.mode$.pipe(
      switchMap((mode) => (mode === 'relay' ? this.relayReturn() : this.popupSignIns())),
      catchError((err) => {
        // provider lookup failed: Google sign-in is unavailable for this page view, password login is not
        console.warn('[google-relay] provider lookup failed', err?.status ?? err);
        return EMPTY;
      })
    );
  }

  /** Relay mode: leave for auth.fsn, which brings the browser back to exactly this URL. */
  async startRelay(): Promise<void> {
    const info = await firstValueFrom(this.provider$);
    if (resolveGoogleLoginMode(info, this.browser.hostname) !== 'relay') {
      return;
    }
    const state = this.browser.randomHex();
    this.browser.setStored(state);
    const returnTo = new URL(this.browser.href);
    returnTo.searchParams.set('relayState', state);
    this.browser.navigate(`${info.relayUrl}/google-relay/start?returnTo=${encodeURIComponent(returnTo.toString())}`);
  }

  private popupSignIns(): Observable<GoogleSignInEvent> {
    return this.socialAuthService.authState.pipe(
      filter((user) => !!user?.idToken),
      map((user) => ({ idToken: user.idToken! }))
    );
  }

  private relayReturn(): Observable<GoogleSignInEvent> {
    return defer(() => from(this.consumeRelayReturn())).pipe(
      filter((event): event is GoogleSignInEvent => event !== null),
      map((event) => {
        if ('error' in event) {
          console.warn('[google-relay] sign-in failed:', event.error);
        }
        return event;
      })
    );
  }

  /**
   * The return leg. The URL is cleaned BEFORE anything else happens, so the one-time code never
   * survives in history, a bookmark or a Referer — and the stored state is consumed whatever the
   * outcome, so a callback URL can only ever be honoured once.
   */
  private async consumeRelayReturn(): Promise<GoogleSignInEvent | null> {
    const url = new URL(this.browser.href);
    const code = url.searchParams.get('code');
    const relayError = url.searchParams.get('relay_error');
    if (!code && !relayError) {
      return null;
    }
    const relayState = url.searchParams.get('relayState');
    const expected = this.browser.getStored();
    this.browser.clearStored();
    RELAY_PARAMS.forEach((p) => url.searchParams.delete(p));
    this.browser.replaceUrl(url.toString());

    if (relayError) {
      return { error: relayError };
    }
    // Login-CSRF guard: an attacker can start a flow as themselves and hand the victim the callback
    // URL; only the browser that stored this state may finish it.
    if (!expected || relayState !== expected) {
      return { error: 'state_mismatch' };
    }
    const info = await firstValueFrom(this.provider$);
    try {
      const response = await this.browser.fetch(
        `${info.relayUrl}/google-relay/token?code=${encodeURIComponent(code!)}`,
        // credentials:'omit' keeps this a simple GET (no preflight) and sends no auth.fsn cookie.
        { credentials: 'omit' }
      );
      if (!response.ok) {
        return { error: `token_${response.status}` };
      }
      const { idToken } = (await response.json()) as { idToken?: unknown };
      if (typeof idToken !== 'string' || !idToken) {
        return { error: 'token_malformed' };
      }
      return { idToken };
    } catch {
      return { error: 'token_unreachable' };
    }
  }
}
